import {Badge, Button, Card} from '@yoast/ui-library';
import {format} from 'date-fns';
import {Link} from 'react-router-dom';

import {useGetAnalysis, useGetBrand, useGetScanQueryResults} from '../../api/generated/insights';
import {AnalysisStatus} from '../../api/generated/insights.schemas';
import CardHeader from '../../components/CardHeader';
import RouteGuardLoading from '../../components/RouteGuards/RouteGuardLoading';
import {useBrandAnalysisData} from '../../contexts';
import {ScanDetailsCard} from './Card';

const ScanDetails = () => {
  const { brandId, analysisId } = useBrandAnalysisData();
  const { data: brandData, isLoading: isLoadingBrand } = useGetBrand(brandId, {
    query: {
      enabled: !!brandId,
      select: ({ data }) => data,
    },
  });

  const { data: analysisData, isLoading: isLoadingAnalysis } = useGetAnalysis(
    brandId,
    analysisId,
    {
      query: {
        enabled: !!brandId && !!analysisId,
        select: ({ data }) => data?.analysis,
      },
    },
  );

  const isLoading = isLoadingBrand || isLoadingAnalysis;
  const hasData =
    analysisData && analysisData.status === AnalysisStatus.completed;

  if (isLoading) {
    return <RouteGuardLoading />;
  }

  if (!hasData) {
    return (
      <Card>
        <CardHeader title="Scan details" />
        <Card.Content>
          <p>No completed analysis found. Please run an analysis first.</p>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Scan details">
        <Button
          as={Link}
          variant="secondary"
          to={`/brands/${brandId}/analysis/${analysisId}`}
        >
          Back to analysis results
        </Button>
      </CardHeader>

      {analysisData.completedScans.map((scan) => (
        <ScanQueryResults
          key={scan.id}
          brandId={brandId}
          analysisId={analysisId}
          scanId={scan.id}
          provider={scan.aiProvider ? String(scan.aiProvider) : 'Unknown'}
          scanFinishedAt={scan.finishedAt}
          brandName={brandData?.name}
        />
      ))}
    </Card>
  );
};

interface ScanQueryResultsProps {
  brandId: string;
  analysisId: string;
  scanId: string;
  provider: string;
  scanFinishedAt: string | null;
  brandName?: string;
}

const ScanQueryResults = ({
  brandId,
  analysisId,
  scanId,
  provider,
  scanFinishedAt,
  brandName,
}: ScanQueryResultsProps) => {
  const { data: queryResultsData, isLoading } = useGetScanQueryResults(
    brandId,
    analysisId,
    scanId,
    {
      query: {
        enabled: !!brandId && !!analysisId && !!scanId,
        select: ({ data }) => data,
      },
    },
  );

  if (isLoading) {
    return <RouteGuardLoading />;
  }

  const queryResults = queryResultsData?.queryResults || [];

  if (queryResults.length === 0) {
    return null;
  }

  return (
    <Card key={scanId} className="yst-mb-4">
      <Card.Content>
        <p className="yst-mb-8">
          {brandName} - Scan performed on:{' '}
          <Badge>
            {scanFinishedAt
              ? format(new Date(scanFinishedAt), 'yyyy-MM-dd HH:mm:ss')
              : 'In progress'}
          </Badge>
          {' '}- AI Platform:{' '}
          <Badge className="yst-bg-green-100 yst-text-green-800">
            {provider}
          </Badge>
        </p>

        {queryResults.map((queryResult) => (
          <ScanDetailsCard
            key={queryResult.id}
            queryResult={queryResult}
          />
        ))}
      </Card.Content>
    </Card>
  );
};

export default ScanDetails;
